import { useEffect, useMemo, useRef, useState } from "react";

import { Close } from "@mui/icons-material";
import {
  Box,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import styles from "./ResizableSidePanel.module.scss";
import { EDGE_MARGIN, wouldSidePanelTakeOver } from "./sidePanelLayout";

const DEFAULT_MIN_WIDTH = 400;
const DEFAULT_MAX_WIDTH = 1200;

export interface ResizableSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  width: number;
  onWidthChange: (width: number) => void;
  minWidth?: number;
  maxWidth?: number;
  // Width that must remain available next to the panel for sibling content
  // (e.g. a primary report area). The effective max width is reduced as the
  // viewport narrows so the panel shrinks before the sibling does.
  minSiblingWidth?: number;
  // Fired when the panel switches to fullscreen takeover mode (mobile or
  // narrow viewport where the panel can't fit alongside its sibling). Lets
  // parents drop drawer-width reservations so the sibling doesn't overflow.
  onTakeoverChange?: (isTakeover: boolean) => void;
  side?: "left" | "right";
  title?: React.ReactNode;
  children: React.ReactNode;
  "data-testid"?: string;
  ariaLabel?: string;
}

const ResizableSidePanel: React.FC<ResizableSidePanelProps> = ({
  isOpen,
  onClose,
  width,
  onWidthChange,
  minWidth = DEFAULT_MIN_WIDTH,
  maxWidth = DEFAULT_MAX_WIDTH,
  minSiblingWidth = 0,
  onTakeoverChange,
  side = "right",
  title,
  children,
  "data-testid": dataTestId,
  ariaLabel,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [isResizing, setIsResizing] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [sidebarWidth, setSidebarWidth] = useState(0);
  const panelScrollRef = useRef<HTMLDivElement>(null);

  // Track the left nav width via the --sidebar-width CSS var that MainLayout
  // sets on documentElement. Updates on window resize (which may flip the
  // mobile breakpoint) and on direct mutation of the style attribute (nav
  // collapse toggle). Without this the takeover threshold and sibling cap
  // are off by the nav width, so the panel can grow over the report.
  useEffect(() => {
    const readSidebar = () => {
      const v = getComputedStyle(document.documentElement).getPropertyValue(
        "--sidebar-width",
      );
      setSidebarWidth(parseInt(v, 10) || 0);
    };
    const onResize = () => {
      setViewportWidth(window.innerWidth);
      readSidebar();
    };
    readSidebar();
    window.addEventListener("resize", onResize);
    const observer = new MutationObserver(readSidebar);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["style"],
    });
    return () => {
      window.removeEventListener("resize", onResize);
      observer.disconnect();
    };
  }, []);

  // Effective horizontal space available for the panel + its sibling.
  const effectiveViewportWidth = viewportWidth - sidebarWidth;

  const effectiveMaxWidth = useMemo(() => {
    const userMax = typeof maxWidth === "number" ? maxWidth : DEFAULT_MAX_WIDTH;
    const siblingCap =
      minSiblingWidth > 0
        ? effectiveViewportWidth - minSiblingWidth - EDGE_MARGIN * 2
        : Infinity;
    return Math.max(
      minWidth,
      Math.min(effectiveViewportWidth * 0.8, userMax, siblingCap),
    );
  }, [effectiveViewportWidth, maxWidth, minSiblingWidth, minWidth]);

  // Narrow viewports get the same fullscreen overlay treatment as mobile.
  const needsTakeover =
    !isMobile &&
    wouldSidePanelTakeOver(minWidth, minSiblingWidth, effectiveViewportWidth);
  const isFullscreen = isMobile || needsTakeover;

  // CSS handles sizing in fullscreen via left:0/right:0 — no JS width needed.
  const displayWidth = isFullscreen ? null : Math.min(width, effectiveMaxWidth);

  // Notify parents when takeover state changes so they can drop drawer-width
  // reservations (otherwise the sibling overflows under a narrow viewport).
  // Emits for both mobile and narrow-viewport takeover — matches the prop
  // contract and means mobile consumers also see drawer-width released.
  useEffect(() => {
    onTakeoverChange?.(isFullscreen);
  }, [isFullscreen, onTakeoverChange]);

  // When the cap shrinks (viewport resize, sibling minWidth change, or a
  // restored width that's larger than the current effective max), report the
  // clamped value back so parents reserving space (--drawer-width etc.) don't
  // hold onto a stale, larger width.
  useEffect(() => {
    if (isFullscreen) return;
    if (width > effectiveMaxWidth) {
      onWidthChange(effectiveMaxWidth);
    }
  }, [width, effectiveMaxWidth, isFullscreen, onWidthChange]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const newWidth =
        side === "right"
          ? window.innerWidth - e.clientX - EDGE_MARGIN
          : e.clientX - EDGE_MARGIN;

      if (newWidth >= minWidth && newWidth <= effectiveMaxWidth) {
        onWidthChange(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "ew-resize";
      document.body.style.userSelect = "none";
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, onWidthChange, minWidth, effectiveMaxWidth, side]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && panelScrollRef.current) {
      panelScrollRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  const handleResizeStart = () => {
    setIsResizing(true);
  };

  if (!isOpen) return null;

  // Resize handle is CSS-hidden on mobile; this style only applies on desktop.
  const resizeHandleStyle =
    displayWidth !== null
      ? side === "right"
        ? { left: `calc(100vw - ${displayWidth}px - ${EDGE_MARGIN}px)` }
        : { left: `${displayWidth + EDGE_MARGIN}px` }
      : {};

  return (
    <>
      {isFullscreen && (
        <Box className={styles.backdrop} onClick={onClose} aria-hidden="true" />
      )}

      {!isFullscreen && (
        <Box
          role="slider"
          aria-orientation="vertical"
          aria-label="Resize side panel"
          aria-valuemin={minWidth}
          aria-valuemax={Math.round(effectiveMaxWidth)}
          aria-valuenow={
            displayWidth !== null ? Math.round(displayWidth) : minWidth
          }
          onMouseDown={handleResizeStart}
          className={`${styles.resizeHandle} ${styles[side]} ${isResizing ? styles.resizing : ""}`}
          style={resizeHandleStyle}
        >
          <Box className={styles.resizeIndicator}>
            <Box className={styles.resizeDot} />
            <Box className={styles.resizeDot} />
            <Box className={styles.resizeDot} />
          </Box>
        </Box>
      )}

      <Box
        ref={panelScrollRef}
        {...(dataTestId ? { "data-testid": dataTestId } : {})}
        {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
        className={`${styles.panel} ${styles[side]} ${isResizing ? styles.resizing : ""} ${isFullscreen ? styles.fullscreen : ""}`}
        style={displayWidth !== null ? { width: `${displayWidth}px` } : {}}
      >
        <Box className={styles.header}>
          <IconButton
            onClick={onClose}
            size="small"
            className={styles.closeButton}
            aria-label="Close panel"
          >
            <Close />
          </IconButton>
          {title != null &&
            (typeof title === "string" || typeof title === "number" ? (
              <Typography variant="h5" className={styles.title}>
                {title}
              </Typography>
            ) : (
              <Box className={styles.titleContent}>{title}</Box>
            ))}
        </Box>

        {children}
      </Box>
    </>
  );
};

export default ResizableSidePanel;
